import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Action } from 'redux';

import { AddressTypeEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import { CountryData, ENROLL_REQUEST_STATE, IndividualExtendedInfo, IndividualExtendedInfoData } from '../../../lib/models/enroll';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import { logout } from '../../../lib/store/common-actions';
import { hasValidCookie } from '../../../lib/store/selectors';
import CountriesCache from '../../../lib/store-util/CountriesCache';
import { checkErrorSatusCode } from '../../../lib/util/error-handling/ErrorHandlingHelpers';
import { NullableNumber, NullableString } from '../../../lib/util/types';
import { createFormControlError, getPhoneNumberValidity, validateControl } from '../../../lib/util/ValidationSchemes/Validate';

import { PhoneAutocomplete } from '../../../components';
import { ICountryControlOptions } from '../../../components/CountryControl/CountryControl.types';
import { PhoneAutocompleteErrors, PhoneAutocompleteValue } from '../../../components/PhoneAutocomplete/PhoneAutocomplete.types';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../../Enrollment/component/Layout';
import { PhoneNumberSelector } from '../Verification.constants';

import styles from './VerificationPhoneNumber.module.scss';

type OwnProps = {};
type Props = OwnProps & ReduxProps;

const VerificationPhoneNumber = (props: Props) => {
  const { sendPhoneCode, appLogOut, isAppLoaded, isAuthenticated, stateData } = props;

  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const countries = CountriesCache.use();

  const requestState = useSelector<RootState, ENROLL_REQUEST_STATE | undefined>((state: RootState) => (
    state.auth.enroll.status
  ));
  const statusCode = useSelector<RootState, number | undefined>((state: RootState) => (
    state.auth.enroll.statusCode
  ));

  const phoneCodeBySelectedCountry = useSelector<RootState, NullableString>(
    (state: RootState) => countries?.find(item => item.id === state.auth.enroll.country)?.phone_code,
  );

  const phoneCode = useSelector<RootState, string | null | undefined>((state: RootState) => (
    state.auth.enroll.phoneCode
  ));
  const phoneNumber = useSelector<RootState, string | null | undefined>((state: RootState) => (
    state.auth.enroll.phoneNumber
  ));
  const individual = useSelector<RootState, IndividualExtendedInfoData | null>((state: RootState) => (
    state.crm.individualExtendedInfo
  ));
  const userId = useSelector<RootState, NullableNumber>((state: RootState) => (
    state.auth.enroll.userId ?? state.auth.enrollUserId ?? state.crm.individualExtendedInfo?.user_correlation_id
  ));

  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [isClickedBtnContinue, setIsClickedBtnContinue] = useState<boolean>(false);
  const [btnDisabled, setBtnDisabled] = useState<boolean>(false);
  const [value, setValue] = useState<PhoneAutocompleteValue>({ phone: '', code: '', countryId: '' });
  const [errors, setErrors] = useState<PhoneAutocompleteErrors>({ phone: [], code: [] });
  const [hasMadeRequest, setHasMadeRequest] = useState<boolean>(false);
  const { form } = EnrollmentState.use();

  const options = useMemo((): ICountryControlOptions[] => {
    if (countries && (countries.length > 0)) {
      return countries
        .filter((country: CountryData) => country.id && country.name && country.phone_code)
        .sort((current: CountryData, next: CountryData) => {
          if (current.name.toLowerCase() < next.name.toLowerCase()) {
            return -1;
          } if (current.name.toLowerCase() > next.name.toLowerCase()) {
            return 1;
          }
          return 0;
        })
        .map((country: CountryData) => ({
          label: country.name,
          value: `${country.phone_code} ${country.name}`,
          countryId: country.id,
        }));
    }

    return [];
  }, [countries]);

  const onNextClick = useCallback(() => {
    const countryCode = value.code.split(' ')[0];
    sendPhoneCode(countryCode, value.phone, userId);
    setHasMadeRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendPhoneCode, userId, value]);

  const validateForm = useCallback((): boolean => {
    const codeCheck = validateControl(
      value.code,
      [],
      lang.commonOnboardingPhoneVerificationCountry(),
      true,
    );

    const phoneCheck = getPhoneNumberValidity(value.phone);

    if (!codeCheck.isValid) {
      setErrors((currState: PhoneAutocompleteErrors) => ({
        ...currState,
        code: codeCheck.errors,
      }));
    }

    if (!phoneCheck.isValid) {
      setErrors((currState: PhoneAutocompleteErrors) => ({
        ...currState,
        phone: phoneCheck.errors,
      }));
    }

    return (codeCheck.isValid && phoneCheck.isValid);
  }, [value]);

  useEffect(() => {
    if (!form.steps.isVerifyEmailPassed && isAppLoaded && !isAuthenticated) {
      navigate(UI_ROUTES.emailConfirm, {
        replace: true,
      });
    } else {
      EnrollmentState.setVerifyEmailStepPassed(true);
    }
  }, [form.steps.isVerifyEmailPassed, isAppLoaded]);// eslint-disable-line react-hooks/exhaustive-deps

  const getCountryCodeValueByPhoneCode = (phoneCountyCode:string) :string => options.find(
    (el) => el.value.toString().includes(phoneCountyCode),
  )!.value.toString();

  useEffect(() => {
    if (!isInitialised) {
      if (phoneCode && phoneNumber) {
        const countryCodeValue = getCountryCodeValueByPhoneCode(phoneCode);

        setValue((currState: PhoneAutocompleteValue) => ({
          ...currState,
          phone: phoneNumber,
          code: countryCodeValue,
        }));
        setIsInitialised(true);
        return;
      }
      if (individual && (individual.addresses && individual.addresses.length > 0)) {
        const individualModel = new IndividualExtendedInfo(individual);
        const residentialAddress = individualModel.getAddressByType(AddressTypeEnum.Residential);

        const filteredOptions = options?.find((option: ICountryControlOptions) => (
          option.countryId === residentialAddress.country_id
        ));

        if (filteredOptions) {
          setValue((currState: PhoneAutocompleteValue) => ({
            ...currState,
            code: filteredOptions.value.toString(),
            countryId: filteredOptions.countryId,
          }));
          setIsInitialised(true);
          return;
        }
      }

      if (phoneCodeBySelectedCountry) {
        setValue((currState: PhoneAutocompleteValue) => ({
          ...currState,
          code: getCountryCodeValueByPhoneCode(phoneCodeBySelectedCountry!),
        }));
        setIsInitialised(true);
      }
    }
  }, [individual, isInitialised, options, phoneCode, phoneCodeBySelectedCountry, phoneNumber]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hasMadeRequest) {
      if (requestState === ENROLL_REQUEST_STATE.READY) {
        navigate(UI_ROUTES.enrollPhoneVerificaionOtp, {
          state: {
            [PhoneNumberSelector]: value,
          },
        });
      } else if (requestState === ENROLL_REQUEST_STATE.ERROR && statusCode) {
        const errorMsg = createFormControlError(
          checkErrorSatusCode(
            statusCode,
            lang.commonOnboardingPhoneVerificationCodeClientErrorMessage(),
            lang.commonOnboardingPhoneVerificationCodeSendAgainErrorMessage(),
          ),
        );
        setHasMadeRequest(false);
        setBtnDisabled(false);
        setErrors((currState: PhoneAutocompleteErrors) => ({
          ...currState,
          phone: [errorMsg],
        }));
      } else {
        setBtnDisabled(true);
      }
    }
  }, [statusCode, hasMadeRequest, navigate, requestState, value]);

  const handleSubmit = () :void => {
    setIsClickedBtnContinue(true);

    if (validateForm()) {
      onNextClick();
    }
  };

  return (
    <Layout
      formTitle=""
      displayBackButton={!isMobile}
      displayNextButton
      nextButtonText={lang.commonContinueButton()}
      displayLoginButton={false}
      onSubmit={handleSubmit}
      onBackBtnPress={appLogOut}
    >
      <div className={styles.text}>
        <div className={styles.title}>{lang.commonVerificationPhoneNeeded()}</div>
        <div className={styles.subtitle}>{lang.commonVerificationPhoneNeededSubtitle()}</div>
      </div>
      <PhoneAutocomplete
        value={value}
        options={options}
        onChange={(controlValue: PhoneAutocompleteValue) => setValue(controlValue)}
        isClickedBtnContinue={isClickedBtnContinue}
      />

    </Layout>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  appLogOut: () => dispatch(logout()),
  sendPhoneCode: (phoneCode: string, phoneNumber: string, userId: NullableNumber) => dispatch(enrollAuth({
    requestType: ENROLL_AUTH_REQUEST_TYPE.SEND_PHONE_CODE,
    phoneCode,
    phoneNumber,
    userId,
  })),
});

const mapStateToProps = (state: RootState) => ({
  isAppLoaded: state.app.loaded,
  stateData: state,
  isAuthenticated: hasValidCookie(state) && state.app.loaded,
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(VerificationPhoneNumber);
